import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEOHead: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  const currentLanguage = i18n.language;
  const alternateLanguage = currentLanguage === 'en' ? 'de' : 'en';
  const baseUrl = window.location.origin;
  const currentUrl = window.location.href;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": t('seo.title'),
    "description": t('seo.description'),
    "url": baseUrl,
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      t('features.pathExtraction'),
      t('features.jsonValidation'),
      t('features.syntaxHighlighting'),
      t('features.searchFilter'),
      t('features.formatting'),
      t('features.json5Support')
    ],
    "creator": {
      "@type": "Organization", 
      "name": "LEANOFY",
      "url": "https://leanofy.de"
    },
    "inLanguage": [
      {
        "@type": "Language",
        "name": "English",
        "alternateName": "en"
      },
      {
        "@type": "Language", 
        "name": "German",
        "alternateName": "de"
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{t('seo.title')}</title>
      <meta name="title" content={t('seo.title')} />
      <meta name="description" content={t('seo.description')} />
      <meta name="keywords" content={t('seo.keywords')} />
      
      {/* Language and Locale */}
      <html lang={currentLanguage} />
      <meta name="language" content={currentLanguage} />
      
      {/* Alternate Language Links */}
      <link rel="alternate" hrefLang={alternateLanguage} href={`${baseUrl}?lang=${alternateLanguage}`} />
      <link rel="alternate" hrefLang="x-default" href={baseUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={t('seo.title')} />
      <meta property="og:description" content={t('seo.description')} />
      <meta property="og:site_name" content="LEANOFY JSON Tools" />
      <meta property="og:locale" content={currentLanguage === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLanguage === 'de' ? 'en_US' : 'de_DE'} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={t('seo.title')} />
      <meta property="twitter:description" content={t('seo.description')} />
      
      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content="LEANOFY" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      
      {/* Technical Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta httpEquiv="Content-Language" content={currentLanguage} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional Tool-specific Meta Tags */}
      <meta name="application-name" content="JSON Path Finder" />
      <meta name="msapplication-TileColor" content="#3B82F6" />
      <meta name="theme-color" content="#3B82F6" />
      
      {/* Tool Keywords (for better categorization) */}
      <meta name="category" content="Developer Tools" />
      <meta name="classification" content="JSON Analysis Tool" />
      <meta name="target" content="Developers, API Testers, Data Analysts" />
    </Helmet>
  );
};

export default SEOHead;